import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { SupportTicketId as SupportTicketIdSchema } from "@redotech/effect-api-shared/ids/definitions";
import {
  PresenceUserId,
  type PresenceUserId as PresenceUserIdType,
} from "@redotech/effect-api-shared/models/team/presence-notification";
import {
  createCollection,
  localOnlyCollectionOptions,
} from "@tanstack/react-db";
import { Schema } from "effect";

export interface SupportTicketPresenceRecord {
  // Composite key: `${supportTicketId}:${userId}`
  id: string;
  supportTicketId: SupportTicketId;
  userId: PresenceUserIdType;
  isViewing: boolean;
  isTyping: boolean;
}

const SupportTicketPresenceRecordSchema = Schema.Struct({
  id: Schema.String,
  supportTicketId: SupportTicketIdSchema,
  userId: PresenceUserId,
  isViewing: Schema.Boolean,
  isTyping: Schema.Boolean,
});

export function createPresenceCollection() {
  // Local-only: presence is realtime, client-only state with no server source,
  // so there is nothing to refetch over the live presence rows. Writes go
  // through the standard insert/update/delete API in the helpers below.
  return createCollection(
    localOnlyCollectionOptions({
      getKey: (item) => item.id,
      schema: Schema.standardSchemaV1(SupportTicketPresenceRecordSchema),
    }),
  );
}

export type PresenceCollection = ReturnType<typeof createPresenceCollection>;

/** Record (or refine) a user's presence on a support ticket. */
export function upsertPresence(
  collection: PresenceCollection,
  record: SupportTicketPresenceRecord,
): void {
  if (collection.has(record.id)) {
    collection.update(record.id, (draft) => {
      draft.supportTicketId = record.supportTicketId;
      draft.userId = record.userId;
      draft.isViewing = record.isViewing;
      draft.isTyping = record.isTyping;
    });
  } else {
    collection.insert(record);
  }
}

/** Drop a presence row (the user stopped viewing/typing). */
export function clearPresence(
  collection: PresenceCollection,
  key: string,
): void {
  if (collection.has(key)) {
    collection.delete(key);
  }
}
